import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Organisation } from '@ghostscreen/contracts';
import { api, ApiRequestError } from '../../lib/api';
import { slugifyOrganisationName } from '../../lib/organisation/slugifyOrganisationName';

const MIN_NAME_LENGTH = 2;

type CreateOrganisationDialogProps = {
  onClose: () => void;
  onCreated: (organisation: Organisation) => void;
};

function createErrorMessage(error: unknown, t: TFunction<'auth'>): string {
  if (error instanceof ApiRequestError && error.status === 409) {
    return t('chooseOrganisation.createDialog.addressTaken');
  }
  return t('chooseOrganisation.createDialog.createFailed');
}

export function CreateOrganisationDialog({ onClose, onCreated }: CreateOrganisationDialogProps) {
  const { t } = useTranslation(['auth', 'common']);
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const slug = slugifyOrganisationName(name);
  const canSubmit = name.trim().length >= MIN_NAME_LENGTH && slug.length > 0;

  const create = useMutation({
    mutationFn: () => api.createOrganisation({ name: name.trim(), slug }),
    onSuccess: async (organisation) => {
      await queryClient.invalidateQueries({ queryKey: ['my-organisations'] });
      onCreated(organisation);
    },
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !create.isPending) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [create.isPending, onClose]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (canSubmit && !create.isPending) create.mutate();
  };

  return (
    <div
      className="org-dialog-backdrop"
      onClick={() => {
        if (!create.isPending) onClose();
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-business-title"
        aria-describedby="create-business-desc"
        className="org-dialog"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="org-dialog__heading">
          <h2 id="create-business-title">{t('chooseOrganisation.createDialog.title')}</h2>
          <p id="create-business-desc">{t('chooseOrganisation.createDialog.description')}</p>
        </div>

        <div className="org-field">
          <label htmlFor="create-business-name" className="org-field__label">
            {t('chooseOrganisation.createDialog.nameLabel')}
          </label>
          <input
            id="create-business-name"
            aria-describedby="create-business-address"
            autoFocus
            className="org-field__input"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              create.reset();
            }}
            placeholder={t('chooseOrganisation.createDialog.namePlaceholder')}
            maxLength={200}
          />
          <span id="create-business-address" className="org-field__hint" dir="ltr">
            ghostscreens.com/{slug || t('chooseOrganisation.createDialog.slugPlaceholder')}
          </span>
        </div>

        {create.isError ? (
          <p role="alert" className="org-dialog__error">
            {createErrorMessage(create.error, t)}
          </p>
        ) : null}

        <div className="org-dialog__actions">
          <button
            type="button"
            className="org-button org-button--secondary"
            disabled={create.isPending}
            onClick={onClose}
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            className="org-button org-button--primary"
            disabled={!canSubmit || create.isPending}
          >
            {create.isPending
              ? t('chooseOrganisation.createDialog.creating')
              : t('chooseOrganisation.createDialog.continue')}
          </button>
        </div>
      </form>
    </div>
  );
}
